import React, { useState } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const documentTypes = [
  { value: '', label: 'Select document type...' },
  { value: 'data-quality', label: 'Data Quality Policy' },
  { value: 'data-retention', label: 'Data Retention Policy' },
  { value: 'data-classification', label: 'Data Classification Policy' },
  { value: 'data-governance-framework', label: 'Data Governance Framework' },
  { value: 'data-privacy', label: 'Data Privacy Policy' },
];

const Icons = {
  FileText: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  AlertTriangle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  CheckCircle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  Database: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  Zap: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Download: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  X: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Shield: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Loader: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation: 'spin 1s linear infinite'}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>,
};

const RiskBadge = ({ level }) => {
  const colors = {
    Critical: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
    High: { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
    Medium: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
    Low: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' }
  };
  const c = colors[level] || colors.Medium;
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace' }}>{level}</span>;
};

const analyzeDocument = async (documentType, documentContent) => {
  const response = await fetch('/.netlify/functions/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentType, documentContent })
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data;
};

const exportToPDF = (results, documentType) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Title
  doc.setFontSize(20);
  doc.setTextColor(15, 23, 42);
  doc.text('Compliance Assessment Report', pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  // Document info
  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  doc.text(`Document Type: ${documentType}`, 20, yPos);
  yPos += 7;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, yPos);
  yPos += 15;

  // Summary section
  doc.setFontSize(14);
  doc.setTextColor(14, 165, 233);
  doc.text('Summary', 20, yPos);
  yPos += 10;

  doc.autoTable({
    startY: yPos,
    head: [['Metric', 'Value']],
    body: [
      ['Overall Risk', results.summary?.overallRisk || 'N/A'],
      ['Compliance Score', `${results.summary?.complianceScore || 0}%`],
      ['Gaps Found', results.summary?.gapsFound || 0],
      ['Regulations', results.summary?.keyRegulations?.join(', ') || 'N/A']
    ],
    theme: 'grid',
    headStyles: { fillColor: [14, 165, 233] },
    margin: { left: 20, right: 20 }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Compliance Gaps
  doc.setFontSize(14);
  doc.setTextColor(245, 158, 11);
  doc.text('Compliance Gaps', 20, yPos);
  yPos += 10;

  const gapsData = (results.gaps || []).map(gap => [
    gap.regulation,
    gap.requirement,
    gap.gap,
    gap.priority
  ]);

  doc.autoTable({
    startY: yPos,
    head: [['Regulation', 'Requirement', 'Gap', 'Priority']],
    body: gapsData,
    theme: 'grid',
    headStyles: { fillColor: [245, 158, 11] },
    margin: { left: 20, right: 20 },
    styles: { fontSize: 8, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 50 },
      2: { cellWidth: 60 },
      3: { cellWidth: 25 }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Check if we need a new page
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  // Impacted Areas
  doc.setFontSize(14);
  doc.setTextColor(14, 165, 233);
  doc.text('Impacted Areas', 20, yPos);
  yPos += 10;

  const areasData = (results.impactedAreas || []).map(area => [
    area.area,
    area.impact,
    area.changes
  ]);

  doc.autoTable({
    startY: yPos,
    head: [['Area', 'Impact', 'Changes Needed']],
    body: areasData,
    theme: 'grid',
    headStyles: { fillColor: [14, 165, 233] },
    margin: { left: 20, right: 20 }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Check if we need a new page
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  // Recommendations
  doc.setFontSize(14);
  doc.setTextColor(34, 197, 94);
  doc.text('Recommendations', 20, yPos);
  yPos += 10;

  const recsData = (results.recommendations || []).map((rec, i) => [
    i + 1,
    rec.action,
    rec.priority
  ]);

  doc.autoTable({
    startY: yPos,
    head: [['#', 'Action', 'Priority']],
    body: recsData,
    theme: 'grid',
    headStyles: { fillColor: [34, 197, 94] },
    margin: { left: 20, right: 20 },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 130 },
      2: { cellWidth: 25 }
    }
  });

  // Save
  doc.save(`compliance-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

const exportToWord = (results, documentType) => {
  const content = `
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
<head><meta charset='utf-8'><title>Compliance Report</title></head>
<body style="font-family: Arial, sans-serif;">
<h1 style="color: #0f172a;">Compliance Assessment Report</h1>
<p><strong>Document Type:</strong> ${documentType}</p>
<p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>

<h2 style="color: #0ea5e9;">Summary</h2>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse;">
<tr><td><strong>Overall Risk</strong></td><td>${results.summary?.overallRisk}</td></tr>
<tr><td><strong>Compliance Score</strong></td><td>${results.summary?.complianceScore}%</td></tr>
<tr><td><strong>Gaps Found</strong></td><td>${results.summary?.gapsFound}</td></tr>
</table>

<p><strong>Regulations Analyzed:</strong> ${results.summary?.keyRegulations?.join(', ')}</p>

<h2 style="color: #f59e0b;">Compliance Gaps</h2>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
<tr style="background: #f8fafc;"><th>Regulation</th><th>Requirement</th><th>Gap</th><th>Priority</th></tr>
${(results.gaps || []).map(gap => `
<tr><td>${gap.regulation}</td><td>${gap.requirement}</td><td>${gap.gap}</td><td>${gap.priority}</td></tr>
`).join('')}
</table>

<h2 style="color: #0ea5e9;">Impacted Areas</h2>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
<tr style="background: #f8fafc;"><th>Area</th><th>Impact</th><th>Changes Needed</th></tr>
${(results.impactedAreas || []).map(area => `
<tr><td>${area.area}</td><td>${area.impact}</td><td>${area.changes}</td></tr>
`).join('')}
</table>

<h2 style="color: #22c55e;">Recommendations</h2>
<table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
<tr style="background: #f8fafc;"><th>#</th><th>Action</th><th>Priority</th></tr>
${(results.recommendations || []).map((rec, i) => `
<tr><td>${i + 1}</td><td>${rec.action}</td><td>${rec.priority}</td></tr>
`).join('')}
</table>

</body></html>
  `;

  const blob = new Blob([content], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function App() {
  const [selectedType, setSelectedType] = useState('');
  const [documentContent, setDocumentContent] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const handleAnalyze = async () => {
    if (!selectedType) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      const analysis = await analyzeDocument(documentTypes.find(d => d.value === selectedType)?.label, documentContent);
      setResults(analysis);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setSelectedType('');
    setDocumentContent('');
    setResults(null);
    setError(null);
  };

  const styles = {
    container: { minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, -apple-system, sans-serif', background: 'linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)', color: '#1e293b' },
    header: { padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', background: 'white' },
    logo: { display: 'flex', alignItems: 'center', gap: '12px' },
    logoIcon: { width: '40px', height: '40px', background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' },
    logoText: { fontSize: '20px', fontWeight: '700', color: '#0f172a' },
    logoAccent: { color: '#0ea5e9' },
    logoSub: { fontSize: '10px', color: '#64748b', letterSpacing: '0.05em', fontFamily: 'JetBrains Mono, monospace' },
    main: { flex: 1, padding: '40px 24px', maxWidth: '1100px', margin: '0 auto', width: '100%' },
    title: { fontSize: '28px', fontWeight: '700', marginBottom: '12px', color: '#0f172a', textAlign: 'center' },
    subtitle: { color: '#64748b', fontSize: '16px', textAlign: 'center', marginBottom: '40px' },
    card: { background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0', maxWidth: '600px', margin: '0 auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    label: { display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#374151' },
    select: { width: '100%', padding: '14px 16px', fontSize: '15px', border: '1px solid #d1d5db', borderRadius: '10px', background: 'white', cursor: 'pointer', outline: 'none', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' },
    textarea: { width: '100%', padding: '14px 16px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '10px', minHeight: '120px', resize: 'vertical', outline: 'none', fontFamily: 'inherit' },
    button: { width: '100%', padding: '16px', fontSize: '16px', fontWeight: '600', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
    buttonActive: { background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)', color: 'white' },
    buttonDisabled: { background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' },
    error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#dc2626', fontSize: '14px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' },
    statCard: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' },
    statLabel: { fontSize: '11px', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', fontFamily: 'JetBrains Mono, monospace' },
    statValue: { fontSize: '24px', fontWeight: '700' },
    section: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
    sectionTitle: { fontSize: '15px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a' },
    item: { background: '#f8fafc', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    recommendation: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '10px' },
    recNumber: { background: '#22c55e', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0 },
    exportMenu: { position: 'absolute', top: '100%', right: 0, marginTop: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, overflow: 'hidden' },
    exportMenuItem: { padding: '10px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #e2e8f0' },
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        select:focus, textarea:focus { border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1); }
        .export-item:hover { background: #f8fafc; }
      `}</style>

      <header style={styles.header}>
        <div style={styles.logo}>
          <div style={styles.logoIcon}><Icons.Shield /></div>
          <div>
            <div style={styles.logoText}>Compliance<span style={styles.logoAccent}>IQ</span></div>
            <div style={styles.logoSub}>REGULATORY ANALYTICS</div>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {!results ? (
          <div>
            <h2 style={styles.title}>Regulatory Compliance Analysis</h2>
            <p style={styles.subtitle}>Paste your policy or procedure document to analyze against regulations</p>

            <div style={styles.card}>
              <div style={{ marginBottom: '24px' }}>
                <label style={styles.label}>Document Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={isAnalyzing}
                  style={{ ...styles.select, color: selectedType ? '#1e293b' : '#9ca3af' }}
                >
                  {documentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={styles.label}>
                  Policy/Procedure Content <span style={{ fontWeight: '400', color: '#dc2626' }}>*required</span>
                </label>
                <textarea
                  value={documentContent}
                  onChange={(e) => setDocumentContent(e.target.value)}
                  disabled={isAnalyzing}
                  placeholder="Paste your banking policy or procedure document content here. The analysis will only work with actual policy/procedure text."
                  style={styles.textarea}
                />
                {documentContent.length > 0 && documentContent.length < 50 && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '8px' }}>Please paste at least 50 characters of policy content</p>
                )}
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                onClick={handleAnalyze}
                disabled={!selectedType || isAnalyzing || documentContent.trim().length < 50}
                style={{ ...styles.button, ...(!selectedType || isAnalyzing || documentContent.trim().length < 50 ? styles.buttonDisabled : styles.buttonActive) }}
              >
                {isAnalyzing ? <><Icons.Loader /> Analyzing...</> : <><Icons.Zap /> Analyze Compliance</>}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <span style={{ color: '#22c55e' }}><Icons.CheckCircle /></span>
                  <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>Compliance Assessment</h2>
                </div>
                <p style={{ color: '#64748b' }}>Analysis of <span style={{ color: '#0ea5e9', fontWeight: '600' }}>{documentTypes.find(d => d.value === selectedType)?.label}</span></p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={handleReset} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
                  <Icons.X /> New Analysis
                </button>
                <div style={{ position: 'relative' }}>
                  <button 
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)', border: 'none', borderRadius: '8px', padding: '10px 16px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}
                  >
                    <Icons.Download /> Export
                  </button>
                  {showExportMenu && (
                    <div style={styles.exportMenu}>
                      <div 
                        className="export-item"
                        style={styles.exportMenuItem}
                        onClick={() => { exportToWord(results, documentTypes.find(d => d.value === selectedType)?.label); setShowExportMenu(false); }}
                      >
                        <Icons.FileText /> Export to Word
                      </div>
                      <div 
                        className="export-item"
                        style={{ ...styles.exportMenuItem, borderBottom: 'none' }}
                        onClick={() => { exportToPDF(results, documentTypes.find(d => d.value === selectedType)?.label); setShowExportMenu(false); }}
                      >
                        <Icons.FileText /> Export to PDF
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Overall Risk</p>
                <p style={{ ...styles.statValue, color: results.summary?.overallRisk === 'Critical' ? '#dc2626' : results.summary?.overallRisk === 'High' ? '#d97706' : '#2563eb' }}>{results.summary?.overallRisk}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Compliance Score</p>
                <p style={styles.statValue}>{results.summary?.complianceScore}%</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Gaps Found</p>
                <p style={styles.statValue}>{results.summary?.gapsFound}</p>
              </div>
            </div>

            {results.summary?.keyRegulations && (
              <div style={{ ...styles.section, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Analyzed against:</span>
                {results.summary.keyRegulations.map((reg, i) => (
                  <span key={i} style={{ background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', fontFamily: 'JetBrains Mono, monospace' }}>{reg}</span>
                ))}
              </div>
            )}

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}><span style={{ color: '#f59e0b' }}><Icons.AlertTriangle /></span> Compliance Gaps</h3>
              {(results.gaps || []).map((gap, i) => (
                <div key={i} style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', borderLeft: `3px solid ${gap.priority === 'High' ? '#f59e0b' : gap.priority === 'Medium' ? '#3b82f6' : '#22c55e'}`, marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontFamily: 'JetBrains Mono, monospace' }}>{gap.regulation}</span>
                    <RiskBadge level={gap.priority} />
                  </div>
                  <p style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px', color: '#1e293b' }}>{gap.requirement}</p>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>{gap.gap}</p>
                  <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'JetBrains Mono, monospace' }}>⏱ {gap.deadline}</span>
                </div>
              ))}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}><span style={{ color: '#0ea5e9' }}><Icons.Database /></span> Impacted Areas</h3>
              {(results.impactedAreas || []).map((area, i) => (
                <div key={i} style={styles.item}>
                  <div>
                    <p style={{ fontWeight: '500', fontSize: '13px', color: '#1e293b' }}>{area.area}</p>
                    <p style={{ fontSize: '11px', color: '#64748b', fontFamily: 'JetBrains Mono, monospace' }}>{area.changes} changes needed</p>
                  </div>
                  <RiskBadge level={area.impact} />
                </div>
              ))}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}><span style={{ color: '#22c55e' }}><Icons.CheckCircle /></span> Recommendations</h3>
              {(results.recommendations || []).map((rec, i) => (
                <div key={i} style={styles.recommendation}>
                  <span style={styles.recNumber}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', color: '#1e293b', marginBottom: '4px' }}>{rec.action}</p>
                    <span style={{ fontSize: '11px', color: rec.priority === 'High' ? '#d97706' : '#2563eb', fontFamily: 'JetBrains Mono, monospace' }}>{rec.priority} Priority</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
